<template>
  <div class="profile">
    <Loading v-if="loading" />
    <Title :title="$t('profile')" />
    <div class="profile__container">
      <FormKit
        type="form"
        id="profile-form"
        v-model="formData"
        :actions="false"
        @submit="submitHandler"
      >
        <Input
          v-for="el in infoFromUser"
          :key="el.id"
          :type="el.type"
          :name="el.name"
          :label="$t(el.label)"
          :validation="el.validation"
          :validationMessages="{
            required: $t('validation.required')
          }"
          :data-testid="el.dataTestId"
        />
        <Input
          v-for="el in infoRole"
          :key="el.id"
          :type="el.type"
          :name="el.name"
          :label="$t(el.label)"
          :disabled="disabledInput(el.name)"
          :validation="el.validation"
          :data-testid="el.dataTestId"
        />
        <div class="profile__logins">
          <div class="profile__logins-block" v-if="showOldLogin">
            <div class="profile__logins-title" v-if="showLoginTitles">
              {{ $t('oldLogin') }}
            </div>
            <Input
              v-for="el in oldLoginFields"
              :key="el.id"
              :type="el.type"
              :name="el.name"
              :label="$t(el.label)"
              :disabled="disabledInput(el.name)"
              :validation="el.validation"
              :validationMessages="{
                required: $t('validation.required'),
                length: $t('validation.length')
              }"
              :data-testid="el.dataTestId"
            />
          </div>
          <div class="profile__logins-block" v-if="showNewLogin">
            <div class="profile__logins-title" v-if="showLoginTitles">
              {{ $t('newLogin') }}
            </div>
            <Input
              v-for="el in newLoginFields"
              :key="el.id"
              :type="el.type"
              :name="el.name"
              :label="$t(el.label)"
              :disabled="disabledInput(el.name)"
              :validation="el.validation"
              :validationMessages="{
                required: $t('validation.required'),
                length: $t('validation.length')
              }"
              :data-testid="el.dataTestId"
            />
          </div>
        </div>
      </FormKit>
      <div class="profile__container-buttons">
        <Button variant="filled" :title="$t('save')" @click="submitForm" />
        <Button
          variant="notBordered"
          :title="$t('cleanFields')"
          @click="cleanForm"
        />
      </div>
      <ChangeLang />
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';

import { infoFromUser } from '@/constants';
import { infoRole, oldLoginFields, newLoginFields } from './configs';

import ChangeLang from './ChangeLang/ChangeLang.vue';
import { DataUpdateUserProfile } from '@/store/modules/account/types';
import { PickupBox } from '@/store/modules/pharmacy/types';

@Options({
  name: 'Profile',
  components: {
    Loading,
    ChangeLang
  },
  data() {
    return {
      formData: {
        firstName: '',
        lastName: '',
        role: '',
        pharmacistPin: '',
        pinCode: '',
        boxLoginPin: '',
        newPinCode: ''
      },
      loading: false,
      infoFromUser,
      infoRole,
      oldLoginFields,
      newLoginFields
    };
  },
  created() {
    this.show();
  },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('pharmacy', ['pharmacy', 'pickupBoxes']),
    // Which login the pharmacy uses is decided by its pick-up boxes: a box
    // with info.newBox is on the new login, every other box is on the old
    // one. A pharmacy with a mix of both keeps both blocks visible.
    showNewLogin() {
      return this.boxes.some((pickupBox: PickupBox) => pickupBox.info?.newBox);
    },
    showOldLogin() {
      return (
        !this.boxes.length ||
        this.boxes.some((pickupBox: PickupBox) => !pickupBox.info?.newBox)
      );
    },
    // With a single block on screen there is nothing to tell apart, so the
    // headings are only needed when the pharmacy shows both logins.
    showLoginTitles() {
      return this.showOldLogin && this.showNewLogin;
    },
    boxes() {
      return this.pickupBoxes || [];
    }
  },
  watch: {
    'formData.pharmacistPin'(value: string) {
      if (this.formData.newPinCode !== value) {
        this.formData.newPinCode = value;
      }
    },
    'formData.newPinCode'(value: string) {
      if (this.formData.pharmacistPin !== value) {
        this.formData.pharmacistPin = value;
      }
    }
  },
  methods: {
    ...mapActions('account', ['getUserProfile', 'updateProfile']),
    ...mapActions('pharmacy', ['getPharmacy', 'getPickupBoxesByPharmacy']),
    async show() {
      this.loading = true;
      await this.getUserProfile();
      await this.getPharmacy(this.user.pharmacyId);
      await this.getPickupBoxesByPharmacy(this.user.pharmacyId);

      this.formData = {
        ...this.formData,
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        role: this.getFriendlyNamesRoles(this.user.role.name),
        pharmacistPin: this.user.pharmacistPin,
        pinCode: this.pharmacy?.pinCode,
        boxLoginPin: this.user.boxLoginPin,
        newPinCode: this.user.pharmacistPin
      };

      this.loading = false;
    },
    getFriendlyNamesRoles(roleName: string) {
      const friendlyNames: Record<string, unknown> = {
        main_user: this.$t('mainUser'),
        user: this.$t('user')
      };

      return friendlyNames[roleName];
    },
    async submitHandler(data: DataUpdateUserProfile) {
      this.loading = true;

      delete data.role;
      delete data.pinCode;
      delete data.boxLoginPin;
      delete data.newPinCode;

      await this.updateProfile(data);
      await this.getUserProfile();
      this.loading = false;
    },
    submitForm() {
      this.$formkit.submit('profile-form');
    },
    cleanForm() {
      this.formData.firstName = '';
      this.formData.lastName = '';
      this.formData.pharmacistPin = '';
      this.formData.newPinCode = '';
    },
    disabledInput(name: string) {
      if (name === 'role' || name === 'pinCode' || name === 'boxLoginPin') {
        return true;
      } else {
        return false;
      }
    }
  }
})
export default class Profile extends Vue {}
</script>

<style lang="scss">
.profile {
  height: 100vh;
  padding: 24px;
  .profile__container {
    padding-top: 32px;
    .formkit-form {
      .input {
        width: 377px;
        margin-bottom: 32px;
      }
    }
    .profile__logins {
      display: flex;
      flex-wrap: wrap;
      gap: 0 60px;
      .profile__logins-block {
        .profile__logins-title {
          margin-bottom: 24px;
          font-family: 'Ubuntu-Medium';
          font-size: $sixteenSize;
          line-height: $eighteenSize;
          font-weight: 500;
          color: $main-text;
        }
      }
    }
    .profile__container-buttons {
      margin-top: 30px;
    }
  }
}
</style>
